/**
 * First-party analytics for GatorNotes.
 * Events are written to the `analytics_events` table (admin-readable only).
 * No estimation or sampling — one row per real user action.
 */
import { supabase } from "@/integrations/supabase/client";

const SESSION_KEY = "gn_analytics_sid";
const ATTR_KEY = "gn_analytics_attr";

type Attribution = {
  referrer: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
};

function uuid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `s-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  }
}

export function getSessionId(): string {
  try {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = uuid();
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return "no-storage-session";
  }
}

/** First-touch attribution for this browser (referrer + UTM tags). */
export function getAttribution(): Attribution {
  const empty: Attribution = {
    referrer: null,
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
  };
  try {
    const stored = localStorage.getItem(ATTR_KEY);
    if (stored) return { ...empty, ...JSON.parse(stored) };

    const params = new URLSearchParams(window.location.search);
    const ref = document.referrer && !document.referrer.includes(window.location.host)
      ? document.referrer
      : "";
    const attr: Attribution = {
      referrer: ref || null,
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
    };
    localStorage.setItem(ATTR_KEY, JSON.stringify(attr));
    return attr;
  } catch {
    return empty;
  }
}

export type AnalyticsEventName =
  | "page_view"
  | "note_view"
  | "buy_click"
  | "checkout_started"
  | "payment_initiated"
  | "purchase_completed"
  | "free_download"
  | "note_download"
  | "signup_started"
  | "signup_completed"
  | "search"
  | "search_result_click"
  | "referral";

/** All event names the dashboard's tracking audit expects to exist. */
export const TRACKED_EVENT_NAMES: AnalyticsEventName[] = [
  "page_view",
  "note_view",
  "buy_click",
  "checkout_started",
  "payment_initiated",
  "purchase_completed",
  "free_download",
  "note_download",
  "signup_started",
  "signup_completed",
  "search",
  "search_result_click",
  "referral",
];

export async function track(
  event: AnalyticsEventName,
  opts: { noteId?: string | null; properties?: Record<string, unknown> } = {}
): Promise<void> {
  try {
    const attr = getAttribution();
    const { data } = await supabase.auth.getSession();
    await supabase.from("analytics_events").insert({
      session_id: getSessionId(),
      user_id: data.session?.user?.id ?? null,
      event_name: event,
      note_id: opts.noteId ?? null,
      path: window.location.pathname,
      referrer: attr.referrer,
      utm_source: attr.utm_source,
      utm_medium: attr.utm_medium,
      utm_campaign: attr.utm_campaign,
      properties: (opts.properties ?? {}) as never,
    });
  } catch {
    // Analytics must never break the app.
  }
}
